import { CommonAddress } from '../../../../types/protocol'
import {
  keyboardCalibration,
  ClassicKeyTravel,
  ClassicKeyTravelInfo,
  KeyTravelFamily,
  KeyTravelList,
  KeyTravelWriteList,
  protocolCailbrationType,
} from '../../../../types/keyTravel'
import { KeyTravelFeature } from '../../../../features/key-travel/contract'
import { sendRead, sendWrite, checkACK } from '../../capabilities/memory'
import { getAddress } from '../../../../utils/common'
import { ADDRESS_CH585M } from '../../protocols/address'
import { CMD_CH585M, PROTOCOL_CONFIG } from '../../protocols/cmd'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { baseKeyMap } from '../../../../types/keyboardKeys'
import { indexToRowCol, isValidRowCol, rowColToIndex } from '../../protocols/addressGenerators'

const REPORT_ID = PROTOCOL_CONFIG.report_id
const FEATURE_REPORT_ID = PROTOCOL_CONFIG.feature_report_id

/**
 * @file CH585MKeys.ts
 * @author Rapoo Device SDK Team
 * @date 2026-03-31
 * @description CH585M 键程功能
 */
export class CH585MTravel extends KeyTravelFeature {
  /**
   * 获取全局RT配置
   */
  async getGlobalRTConfig(): Promise<number> {
    console.log('[CH585MTravel] get global RT config')
    const ADDRESS = getAddress(ADDRESS_CH585M.GLOBAL_RT_ID, this.device.currentConfig.index)
    return sendRead(
      this.device,
      ADDRESS,
      1,
      (dv: DataView) => {
        return dv.getUint8(4)
      },
      // travel:global 供物理 RT 切换键上报路径失效(tag-mapping §1 场景B);cfg 供配置切换失效
      [`cfg:${this.device.currentConfig.index}`, 'travel:global'],
    )
  }
  /**
   * 设置全局RT配置
   */
  async setGlobalRTConfig(index: number): Promise<void> {
    console.log('[CH585MTravel] set global RT config')
    const ADDRESS = getAddress(ADDRESS_CH585M.GLOBAL_RT_ID, this.device.currentConfig.index)
    const realID = Math.min(Math.max(index, 0), 3)
    return sendWrite(this.device, ADDRESS, [realID])
  }
  /**
   * 获取键盘所有键位的单键程及死区
   */
  async getKeyTravel(rt: number): Promise<KeyTravelList> {
    console.log('[CH585M] get key travel')
    if (rt >= 0 && rt <= 2) {
      const globalRTAddresses = [
        ADDRESS_CH585M.GLOBAL_RT_0_TRAVEL,
        ADDRESS_CH585M.GLOBAL_RT_1_TRAVEL,
        ADDRESS_CH585M.GLOBAL_RT_2_TRAVEL,
      ]
      const ADDRESS = getAddress(globalRTAddresses[rt], this.device.currentConfig.index)

      const globalKeyTravel = await sendRead(this.device, ADDRESS, 8, (dv: DataView) => {
        return this.parseKeyTravelData(dv) // 复用通用解析函数
      })

      return [
        {
          family: KeyTravelFamily.Classic,
          index: 0,
          keyTravel: globalKeyTravel,
        },
      ]
    }

    const ADDRESSMap = ADDRESS_CH585M.RT_3_TRAVEL_LIST[this.device.currentConfig.index]

    const LayoutKeys = this.device.manifest.layout.keys
    const allKeys: Array<{ row: number; col: number; address: CommonAddress }> = []
    // 不过滤，保持地址连续性（硬件地址是 6×22 连续定长矩阵，每键 8 字节）
    for (const rowStr in ADDRESSMap) {
      const row = Number(rowStr)
      const colMap = ADDRESSMap[rowStr]
      for (const colStr in colMap) {
        const col = Number(colStr)
        allKeys.push({ row, col, address: colMap[colStr] })
      }
    }

    // 按3个键一组分组（24字节批量读取）
    const keyGroups: Array<typeof allKeys> = []
    for (let i = 0; i < allKeys.length; i += 3) {
      keyGroups.push(allKeys.slice(i, i + 3))
    }

    const readPromises: Promise<ClassicKeyTravelInfo[]>[] = []

    for (const group of keyGroups) {
      // 跳过整组无效的按键（避免无意义的sendRead）
      const hasValidKey = group.some((k) => isValidRowCol(k.row, k.col, LayoutKeys))
      if (!hasValidKey) {
        continue
      }

      const firstAddr = group[0].address
      readPromises.push(
        sendRead(this.device, firstAddr, 24, (dv: DataView) => {
          const result: ClassicKeyTravelInfo[] = []
          const BASE_OFFSET = 4

          for (let i = 0; i < group.length; i++) {
            const keyOffset = BASE_OFFSET + i * 8
            if (keyOffset + 8 > dv.byteLength) break

            const { row, col } = group[i]
            const index = rowColToIndex(row, col)
            // 这里才过滤无效按键（地址偏移已正确计算）
            if (!isValidRowCol(row, col, LayoutKeys)) {
              continue
            }

            const keyTravel = this.parseKeyTravelData(dv, keyOffset)
            result.push({ family: KeyTravelFamily.Classic, index, keyTravel })
          }

          return result
        }),
      )
    }

    return (await Promise.all(readPromises)).flat()
  }

  /**
   * 【流式】获取键盘所有键位的单键程及死区
   * @param batchSize 每批读取的键位数量（默认22个）
   * @returns 异步迭代器，逐批返回 KeyTravelInfo 数组
   */
  async *getKeyTravelStream(rt: number, batchSize: number = 22): AsyncIterable<KeyTravelList> {
    console.log('[CH585M] Streaming key travel data')
    if (rt >= 0 && rt <= 2) {
      const globalRTAddresses = [
        ADDRESS_CH585M.GLOBAL_RT_0_TRAVEL,
        ADDRESS_CH585M.GLOBAL_RT_1_TRAVEL,
        ADDRESS_CH585M.GLOBAL_RT_2_TRAVEL,
      ]
      const ADDRESS = getAddress(globalRTAddresses[rt], this.device.currentConfig.index)

      // 读取8字节全局配置
      const globalKeyTravel = await sendRead(this.device, ADDRESS, 8, (dv: DataView) => {
        return this.parseKeyTravelData(dv)
      })

      // 流式返回长度1的数组
      yield [
        {
          family: KeyTravelFamily.Classic,
          index: 0,
          keyTravel: globalKeyTravel,
        },
      ]
      return
    }

    // 1. 收集所有单个键位的行/列/地址信息（不过滤，保持地址连续性）
    const ADDRESSMap = ADDRESS_CH585M.RT_3_TRAVEL_LIST[this.device.currentConfig.index]
    const allKeys: Array<{ row: number; col: number; address: CommonAddress }> = []

    const LayoutKeys = this.device.manifest.layout.keys
    for (const rowStr in ADDRESSMap) {
      const row = Number(rowStr)
      const colMap = ADDRESSMap[rowStr]

      for (const colStr in colMap) {
        const col = Number(colStr)
        allKeys.push({ row, col, address: colMap[colStr] })
      }
    }

    // 2. 按【键位数量】分批
    const totalKeys = allKeys.length
    for (let start = 0; start < totalKeys; start += batchSize) {
      const end = Math.min(start + batchSize, totalKeys)
      const batchKeys = allKeys.slice(start, end) // 本次批次的所有单键（数量=batchSize，最后一批可能不足）

      // 3. 批内按硬件规则分组（3个键一组，共用第一个键的地址读取24字节）
      const batchKeyGroups: Array<typeof batchKeys> = []
      for (let i = 0; i < batchKeys.length; i += 3) {
        batchKeyGroups.push(batchKeys.slice(i, i + 3))
      }

      // 4. 并发读取当前批次内所有键组的数据
      const batchGroupPromises = batchKeyGroups
        .filter((group) => group.some((k) => isValidRowCol(k.row, k.col, LayoutKeys)))
        .map((group) => {
          return new Promise<ClassicKeyTravelInfo[]>((resolve) => {
            // 每组共用第一个键的地址读取24字节（3个键 × 8字节/键）
            const firstAddr = group[0].address
            sendRead(this.device, firstAddr, 24, (dv: DataView) => {
              const groupResult: ClassicKeyTravelInfo[] = []
              const BASE_OFFSET = 4 // 有效数据起始偏移

              for (let i = 0; i < group.length; i++) {
                const keyOffset = BASE_OFFSET + i * 8
                // 边界保护：避免数据长度不足导致解析异常
                if (keyOffset + 8 > dv.byteLength) break

                const { row, col } = group[i]
                const index = rowColToIndex(row, col)
                // 这里才过滤无效按键（地址偏移已正确计算）
                if (!isValidRowCol(row, col, LayoutKeys)) {
                  continue
                }

                const keyTravel = this.parseKeyTravelData(dv, keyOffset)
                groupResult.push({
                  family: KeyTravelFamily.Classic,
                  index,
                  keyTravel,
                })
              }

              resolve(groupResult)
            })
          })
        })

      // 5. 等待当前批次所有组读取完成，扁平化为单键列表并流式返回
      const batchGroupResults = await Promise.all(batchGroupPromises)
      const batchResult = batchGroupResults.flat() // 合并组结果为单键列表
      yield batchResult

      await new Promise((resolve) => setTimeout(resolve, 0))
    }
  }

  /**
   * 解析8字节键程数据为KeyTravel对象（单键程及死区格式表）
   * @param dv DataView 读取到的二进制数据
   * @param offset 8字节数据的起始偏移（默认4，对应有效数据起始位）
   * @returns ClassicKeyTravel 解析后的键程配置
   */
  private parseKeyTravelData(dv: DataView, offset: number = 4): ClassicKeyTravel {
    const b0 = dv.getUint8(offset + 0)
    const b1 = dv.getUint8(offset + 1)
    const b2 = dv.getUint8(offset + 2)
    const b3 = dv.getUint8(offset + 3)
    const b4 = dv.getUint8(offset + 4)

    /**
     * 按【单键程及死区格式表】逐字节/逐位解析8字节单键配置数据
     * 数据映射关系（b0~b7 对应格式表 字节1~字节8）：
     * b0(字节1): 绝对触发位置 低8位
     * b1(字节2): 低2位 → 绝对触发位置 高2位； 高6位 → RT快速触发灵敏度 低6位
     * b2(字节3): 低4位 → RT快速触发灵敏度 高4位； 高4位 → RT快速重置灵敏度 低4位
     * b3(字节4): 低6位 → RT快速重置灵敏度 高6位； 位6 → RT快速触发 开/关； 位7 → 绝对触发 开/关
     * b4(字节5): 单键底部死区（0~50 → 0.00~0.50mm）
     * b5~b7(字节6~8): 保留（固定0xffffff，忽略）
     *
     * 通用取值规则：
     * - 行程类（绝对触发/RT触发/RT重置）：1~350 → 对应 0.01~3.50mm
     * - 开关类：0=关，1=开
     * - 底部死区：0~50 → 对应 0.00~0.50mm
     */

    // 绝对触发位置 = 字节2低2位(高2位) << 8 | 字节1(低8位)
    // 范围：1~350 → 0.01~3.50mm，默认100(1.00mm)
    const absoluteTrigger = ((b1 & 0x03) << 8) | b0

    // RT快速触发灵敏度 = 字节3低4位(高4位) << 6 | 字节2高6位(低6位)
    // 范围：1~350 → 0.01~3.50mm，默认20(0.20mm)
    const rtTriggerSensitivity = ((b2 & 0x0f) << 6) | (b1 >> 2)

    // RT快速重置灵敏度 = 字节4低6位(高6位) << 4 | 字节3高4位(低4位)
    // 范围：1~350 → 0.01~3.50mm，默认20(0.20mm)
    const rtResetSensitivity = ((b3 & 0x3f) << 4) | (b2 >> 4)

    // RT快速触发 开/关 → 字节4 位6
    // 0=关，1=开，默认0
    const rtTriggerEnable = ((b3 >> 6) & 0x01) === 1

    // 绝对触发 开/关 → 字节4 位7
    // 软件端写入时默认写死为1(开)，0=关，1=开
    const absoluteTriggerEnable = ((b3 >> 7) & 0x01) === 1

    // 单键底部死区 → 直接取字节5
    // 范围：0~50 → 0.00~0.50mm，默认0
    const bottomDeadZone = b4

    return {
      absoluteTrigger,
      rtTriggerSensitivity,
      rtResetSensitivity,
      rtTriggerEnable,
      absoluteTriggerEnable,
      bottomDeadZone,
    }
  }

  /**
   * 批量设置多个指定键位的单键程及死区
   * @param keyTravelList 多键位的键程配置列表
   * @returns Promise<void>
   */
  async setKeyTravel(keyTravelList: KeyTravelWriteList, index?: number): Promise<void[] | void> {
    if (!Array.isArray(keyTravelList) || keyTravelList.length === 0) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Invalid key travel list')
    }

    if (index !== undefined && index >= 0 && index <= 2) {
      console.log('[CH585M] Setting key travel data')
      const rt = [
        ADDRESS_CH585M.GLOBAL_RT_0_TRAVEL,
        ADDRESS_CH585M.GLOBAL_RT_1_TRAVEL,
        ADDRESS_CH585M.GLOBAL_RT_2_TRAVEL,
      ]
      if (index > rt.length - 1) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Invalid RT ID')
      }
      const ADDRESS = getAddress(rt[index], this.device.currentConfig.index)
      const sendData = this.dispatchKeyTravel(this.requireClassic(keyTravelList[0]))
      return sendWrite(this.device, ADDRESS, sendData)
    }
    // 批量处理每个键位的配置（并发执行，保证效率）
    const writePromises = keyTravelList.map(async (keyTravelInfo) => {
      const classic = this.requireClassic(keyTravelInfo)
      const { index, keyTravel } = classic
      if (keyTravel == null) {
        return
      }
      console.log('[CH585M] Setting key travel data')

      // 计算当前键位的硬件地址
      const { row, col } = indexToRowCol(index)
      const ADDRESS = getAddress(
        ADDRESS_CH585M.RT_3_TRAVEL_LIST,
        this.device.currentConfig.index,
        row,
        col,
      )

      const sendData = this.dispatchKeyTravel(classic)
      return sendWrite(this.device, ADDRESS, sendData)
    })

    // 等待所有键位的配置写入完成
    return await Promise.all(writePromises)
  }
  /**
   * 解析键程数据为buffer数据
   * @param keyTravelInfo
   * @returns number[]
   */
  private requireClassic(info: KeyTravelWriteList[number]): ClassicKeyTravelInfo {
    if (info.family !== KeyTravelFamily.Classic) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        'CH585M setKeyTravel requires family: KeyTravelFamily.Classic',
      )
    }
    return info
  }

  private dispatchKeyTravel(keyTravelInfo: ClassicKeyTravelInfo): number[] {
    const { keyTravel } = keyTravelInfo

    // 1. 提取并校验键程参数
    // 绝对触发位置：1~350 → 对应 0.01~3.50mm
    const absoluteTrigger = Math.min(Math.max(keyTravel.absoluteTrigger, 1), 350)
    // RT快速触发灵敏度：1~350 → 对应 0.01~3.50mm
    const rtTriggerSensitivity = Math.min(Math.max(keyTravel.rtTriggerSensitivity, 1), 350)
    // RT快速重置灵敏度：1~350 → 对应 0.01~3.50mm
    const rtResetSensitivity = Math.min(Math.max(keyTravel.rtResetSensitivity, 1), 350)
    // RT快速触发开关：布尔转0/1
    const rtTriggerEnable = keyTravel.rtTriggerEnable ? 1 : 0
    // 绝对触发开关：布尔转0/1（软件端默认写死为1，说是没用上，这里保持一致）
    // const absoluteTriggerEnable = keyTravel.absoluteTriggerEnable ? 1 : 0
    const absoluteTriggerEnable = 1
    // 单键底部死区：0~50 → 对应 0.00~0.50mm
    const bottomDeadZone = Math.min(Math.max(keyTravel.bottomDeadZone, 0), 50)

    // 2. 拆分参数到对应字节/位（反向解析逻辑）
    // ---------------- 字节0 (b0)：绝对触发位置 低8位 ----------------
    const b0 = absoluteTrigger & 0xff

    // ---------------- 字节1 (b1)：高6位=RT触发灵敏度低6位 | 低2位=绝对触发位置高2位 ----------------
    const absoluteTrigger_high2 = (absoluteTrigger >> 8) & 0x03 // 绝对触发高2位
    const rtTrigger_low6 = rtTriggerSensitivity & 0x3f // RT触发灵敏度低6位
    const b1 = (rtTrigger_low6 << 2) | absoluteTrigger_high2

    // ---------------- 字节2 (b2)：高4位=RT重置灵敏度低4位 | 低4位=RT触发灵敏度高4位 ----------------
    const rtTrigger_high4 = (rtTriggerSensitivity >> 6) & 0x0f // RT触发灵敏度高4位
    const rtReset_low4 = rtResetSensitivity & 0x0f // RT重置灵敏度低4位
    const b2 = (rtReset_low4 << 4) | rtTrigger_high4

    // ---------------- 字节3 (b3)：低6位=RT重置灵敏度高6位 | 位6=RT触发开关 | 位7=绝对触发开关 ----------------
    const rtReset_high6 = (rtResetSensitivity >> 4) & 0x3f // RT重置灵敏度高6位
    const rtTriggerEnable_bit6 = rtTriggerEnable << 6 // 位6
    const absoluteTriggerEnable_bit7 = absoluteTriggerEnable << 7 // 位7
    const b3 = rtReset_high6 | rtTriggerEnable_bit6 | absoluteTriggerEnable_bit7

    // ---------------- 字节4 (b4)：底部死区（直接赋值） ----------------
    const b4 = bottomDeadZone

    // ---------------- 字节5~7 (b5~b7)：保留位（固定填0） ----------------
    const b5 = 0x00
    const b6 = 0x00
    const b7 = 0x00

    return [b0, b1, b2, b3, b4, b5, b6, b7]
  }
  /**
   * 切换演示模式
   * @param state true: 开启演示模式，false: 关闭演示模式
   */
  async switchDemoMode(state: boolean): Promise<void> {
    const cmd = CMD_CH585M.demo_mode
    const connectionType = this.device.connectionType
    const payload = new Uint8Array([connectionType, cmd[state ? 0 : 1]])
    const res = await this.device.sendAndWait(REPORT_ID, payload, FEATURE_REPORT_ID, (res) => {
      const ack = res.getUint8(0)
      return ack !== 0x02
    })
    checkACK(res)
  }

  /**
   * 进入键盘校准模式
   */
  async switchCalibrationMode(state: boolean, options?: keyboardCalibration): Promise<void> {
    const cmd = CMD_CH585M.keyboard_calibration
    const connectionType = this.device.connectionType
    // 默认模式为0x02 即只开启了SampleMaxDelta
    const { ati = 0, smd = 1, user = 0 } = options || {}
    let calSel = 0x00
    // bit0: ATI校准
    if (ati) calSel |= 1 << 0
    // bit1: SampleMaxDelta校准
    if (smd) calSel |= 1 << 1
    // bit2: 工厂/用户校准
    if (user) calSel |= 1 << 2
    const payload = new Uint8Array([connectionType, cmd[state ? 0 : 1], calSel])
    const res = await this.device.sendAndWait(REPORT_ID, payload, FEATURE_REPORT_ID, (res) => {
      const ack = res.getUint8(0)
      return ack !== 0x02
    })
    // 写入成功通常 ACK 为 0x01 (或者其他非 0x02)
    checkACK(res)
  }

  /**
   * 协议校准
   */
  async calibration(type: protocolCailbrationType, _keyList?: baseKeyMap): Promise<void> {
    let cmd = [] as number[]
    if (type === 'ATI') cmd = [...CMD_CH585M.ATI_calibration]
    if (type === 'SMD') cmd = [...CMD_CH585M.SMD_calibration]
    if (!cmd.length) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Invalid calibration type')
    }
    const connectionType = this.device.connectionType
    // 默认全键校准暂不处理keyList
    const sendData = Array(18).fill(0xff)
    const payload = new Uint8Array([connectionType, cmd[0], ...sendData])
    console.log('calibration payload', payload)
    const res = await this.device.sendAndWait(REPORT_ID, payload, FEATURE_REPORT_ID, (res) => {
      const ack = res.getUint8(0)
      return ack !== 0x02
    })
    checkACK(res)
  }

  /**
   * 保存协议校准数据
   */
  async saveCalibration(type: protocolCailbrationType): Promise<void> {
    let cmd = [] as number[]
    if (type === 'ATI') cmd = [...CMD_CH585M.ATI_calibration]
    if (type === 'SMD') cmd = [...CMD_CH585M.SMD_calibration]
    if (!cmd.length) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Invalid calibration type')
    }
    const connectionType = this.device.connectionType
    const payload = new Uint8Array([connectionType, cmd[1]])
    const res = await this.device.sendAndWait(REPORT_ID, payload, FEATURE_REPORT_ID, (res) => {
      const ack = res.getUint8(0)
      return ack !== 0x02
    })
    checkACK(res)
  }
  /**
   * 切换自适应校准
   */
  async switchAdaptiveCalibration(state: boolean): Promise<void> {
    const factoryAddr = getAddress(ADDRESS_CH585M.ADATIVE_CALIBRATION_ENABLE, 0)
    const userAddr = getAddress(ADDRESS_CH585M.ADATIVE_CALIBRATION_ENABLE, 2)
    let sendData = Array(8).fill(0x00)
    if (state) {
      sendData = sendData.map((_, index) => (index % 2 === 0 ? 0x07 : 0x00))
    }
    await sendWrite(this.device, factoryAddr, sendData)
    await sendWrite(this.device, userAddr, sendData)
  }
}
